import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Login from "./pages/Login";
import Shell from "./pages/Shell";
import Dashboard from "./pages/Dashboard";
import Inventory from "./pages/Inventory";
import Production from "./pages/Production";
import Batches from "./pages/Batches";
import Attendance from "./pages/Attendance";
import EmployeeAttendance from "./pages/EmployeeAttendance";
import Meetings from "./pages/Meetings";
import Documents from "./pages/Documents";
import Accounting from "./pages/Accounting";
import Loans from "./pages/Loans";
import Users from "./pages/Users";
import Subscriptions from "./pages/Subscriptions";
import Issues from "./pages/Issues";
import Parties from "./pages/Parties";
import Settings, { applyTheme } from "./pages/Settings";
import Employees from "./pages/Employees";
import Products from "./pages/Products";
import MiscPurchases from "./pages/MiscPurchases";
import CompanyExpenses from "./pages/CompanyExpenses";
import GstReports from "./pages/GstReports";
import AuditLog from "./pages/AuditLog";
import RawMaterials from "./pages/RawMaterials";
import PnL from "./pages/PnL";
import Barcodes from "./pages/Barcodes";
import Requests from "./pages/Requests";
import Landing from "./pages/Landing";
import Enquiries from "./pages/Enquiries";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { ThemeProvider } from "./lib/theme";
import AccessDenied from "./pages/AccessDenied";

// Apply saved theme on app load
try {
  const saved = localStorage.getItem("kr_theme");
  if (saved) applyTheme(JSON.parse(saved));
} catch (e) {
  console.warn("Failed to apply saved theme", e);
}

function Protected({ screen, children }) {
  const { user, loading, can } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (screen && !can(screen)) return <AccessDenied screen={screen} />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <Toaster position="top-right" theme="light" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/app" element={<Protected><Shell /></Protected>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
          </Route>
          <Route element={<Protected><Shell /></Protected>}>
            <Route path="dashboard" element={<Protected screen="dashboard"><Dashboard /></Protected>} />
            <Route path="inventory" element={<Protected screen="inventory"><Inventory /></Protected>} />
            <Route path="production" element={<Protected screen="production"><Production /></Protected>} />
            <Route path="batches" element={<Protected screen="batches"><Batches /></Protected>} />
            <Route path="attendance" element={<Protected screen="attendance"><Attendance /></Protected>} />
            <Route path="attendance/employee/:eid" element={<Protected screen="attendance"><EmployeeAttendance /></Protected>} />
            <Route path="meetings" element={<Protected screen="meetings"><Meetings /></Protected>} />
            <Route path="quotations" element={<Protected screen="quotations"><Documents type="quotation" /></Protected>} />
            <Route path="purchase-orders" element={<Protected screen="purchase_orders"><Documents type="purchase_order" /></Protected>} />
            <Route path="purchase-invoices" element={<Protected screen="misc_purchases"><MiscPurchases /></Protected>} />
            <Route path="misc-purchases" element={<Protected screen="misc_purchases"><MiscPurchases /></Protected>} />
            <Route path="products" element={<Protected screen="products"><Products /></Protected>} />
            <Route path="pnl" element={<Protected screen="pnl"><PnL /></Protected>} />
            <Route path="barcodes" element={<Protected screen="barcodes"><Barcodes /></Protected>} />
            <Route path="sales-invoices" element={<Protected screen="sales_invoices"><Documents type="sales_invoice" /></Protected>} />
            <Route path="accounting" element={<Protected screen="accounting"><Accounting /></Protected>} />
            <Route path="loans" element={<Protected screen="accounting"><Loans /></Protected>} />
            <Route path="subscriptions" element={<Protected screen="subscriptions"><Subscriptions /></Protected>} />
            <Route path="issues" element={<Protected screen="issues"><Issues /></Protected>} />
            <Route path="customers" element={<Protected screen="customers"><Parties kind="customer" /></Protected>} />
            <Route path="suppliers" element={<Protected screen="suppliers"><Parties kind="supplier" /></Protected>} />
            <Route path="employees" element={<Protected screen="users"><Employees /></Protected>} />
            <Route path="settings" element={<Protected screen="settings"><Settings /></Protected>} />
            <Route path="users" element={<Protected screen="users"><Users /></Protected>} />
            <Route path="requests" element={<Protected><Requests /></Protected>} />
            <Route path="enquiries" element={<Protected screen="users"><Enquiries /></Protected>} />
            <Route path="company-expenses" element={<Protected screen="users"><CompanyExpenses /></Protected>} />
            <Route path="gst-reports" element={<Protected screen="users"><GstReports /></Protected>} />
            <Route path="audit-log" element={<Protected screen="audit"><AuditLog /></Protected>} />
            <Route path="raw-materials" element={<Protected screen="inventory"><RawMaterials /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
        </ConfirmProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
